// src/plugins/axios.ts — v0.2
import axios, { AxiosError, AxiosRequestConfig } from 'axios'

declare module 'axios' {
  export interface AxiosRequestConfig { silent?: boolean }
}

const api = axios.create({
  baseURL: 'https://rateify.me/',   // note the trailing slash
  withCredentials: true,
  timeout: 15000,
})

// Only set JSON header when needed
api.interceptors.request.use((cfg) => {
  const isFormLike =
    cfg.data instanceof URLSearchParams ||
    (typeof FormData !== 'undefined' && cfg.data instanceof FormData)
  if (!isFormLike && cfg.method?.toLowerCase() === 'post' && !cfg.headers?.['Content-Type']) {
    cfg.headers = cfg.headers || {}
    cfg.headers['Content-Type'] = 'application/json'
  }
  return cfg
})

api.interceptors.response.use(
  r => r,
  (error: AxiosError<any>) => {
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'Network Error'
    if (!error.config?.silent) console.error('[API]', message, error)
    return Promise.reject(error)
  }
)

export default api

export async function postWithRetry<T = any>(
  url: string,
  data?: any,
  cfg: AxiosRequestConfig = {},
  { retries = 2, backoffMs = 1000 }: { retries?: number; backoffMs?: number } = {}
) {
  let attempt = 0;
  const baseCfg: AxiosRequestConfig = { timeout: 0, ...cfg };
  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms + Math.random() * 250));

  while (true) {
    try {
      return await api.post<T>(url, data, baseCfg);
    } catch (e: any) {
      // don’t retry if the request was deliberately cancelled
      if (axios.isCancel?.(e)) throw e;

      const status = e?.response?.status;
      const code = e?.code; // e.g., 'ECONNABORTED', 'ERR_NETWORK'
      const noResponse = !e?.response; // DNS drop, CORS preflight fail, offline, etc.

      const retriable =
        noResponse ||                      // true network error
        code === "ECONNABORTED" ||         // timeout
        code === "ERR_NETWORK" ||          // axios network error
        status === 408 || status === 429 || (status >= 500 && status < 600);

      if (!retriable || attempt >= retries) throw e;
      attempt++;
      await sleep(backoffMs * attempt);
    }
  }
}

